import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Checkbox,
  ListItemText,
  Autocomplete,
  Chip,
} from "@mui/material";
import { getAllJobTitles } from "../../../../core/services/jobTitle.service";
import { getAllGlobalRoles } from "../../../../core/services/globalRole.service";
import { getAllSkills } from "../../../../core/services/skills.service";
import { getAllExpertise } from "../../../../core/services/expertise.service";
import { getRoles } from "../../../../core/services/keycloakApi";

const inputStyle = {
  minWidth: 0,
  width: "100%",
  "& .MuiOutlinedInput-root": {
    color: "#f5f5f5",
    fontSize: 13,
    height: 42,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.04)",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(245, 245, 245, 0.8)",
      borderWidth: "1.5px",
    },
    "&.Mui-focused": {
      background: "rgba(255, 255, 255, 0.08)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 13,
    "&.Mui-focused": {
      color: "#f5f5f5",
    },
  },
  "& .MuiSvgIcon-root": {
    color: "rgba(255, 255, 255, 0.6)",
  },
};

const creatableSelectStyle = {
  ...inputStyle,
  "& .MuiOutlinedInput-root": {
    ...inputStyle["& .MuiOutlinedInput-root"],
    height: "auto",
    minHeight: 42,
    py: 0.25,
    alignItems: "center",
  },
};

const FieldRow = ({ children }) => (
  <Stack
    direction="row"
    spacing={1.75}
    sx={{ minWidth: 0, width: "100%" }}
  >
    {children}
  </Stack>
);

const fieldFlex = { ...inputStyle, flex: 1, minWidth: 0 };
const creatableFieldFlex = { ...creatableSelectStyle, flex: 1, minWidth: 0 };

const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      background: "rgba(20, 20, 20, 0.7)",
      backdropFilter: "blur(15px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      color: "#f5f5f5",
      "& .MuiMenuItem-root": {
        fontSize: 14,
        py: 1.5,
        "&:hover": {
          background: "rgba(255, 255, 255, 0.1)",
        },
        "&.Mui-selected": {
          background: "rgba(255, 255, 255, 0.15)",
          fontWeight: 600,
          "&:hover": { background: "rgba(255, 255, 255, 0.2)" },
        },
      },
    },
  },
};

const autocompletePaperSx = {
  mt: 1,
  background: "rgba(20, 20, 20, 0.7)",
  backdropFilter: "blur(15px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  color: "#f5f5f5",
  "& .MuiAutocomplete-option": {
    fontSize: 14,
    py: 1.5,
    "&:hover": {
      background: "rgba(255, 255, 255, 0.1)",
    },
    "&[aria-selected='true']": {
      background: "rgba(255, 255, 255, 0.15)",
      fontWeight: 600,
      "&:hover": { background: "rgba(255, 255, 255, 0.2)" },
    },
    "&.Mui-focused": {
      background: "rgba(255, 255, 255, 0.1)",
    },
    "&[aria-selected='true'].Mui-focused": {
      background: "rgba(255, 255, 255, 0.15)",
    },
  },
};

const chipSx = {
  height: 24,
  fontSize: 12,
  color: "#f5f5f5",
  background: "rgba(255, 255, 255, 0.12)",
  "& .MuiChip-deleteIcon": {
    color: "rgba(255, 255, 255, 0.55)",
    fontSize: 16,
    "&:hover": { color: "#f5f5f5" },
  },
};

const renderMultiSelectValue = (selectedIds, options, getId, getLabel) => {
  if (!selectedIds?.length) return "";

  return selectedIds
    .map((id) => options.find((option) => getId(option) === id))
    .filter(Boolean)
    .map(getLabel)
    .join(", ");
};

const normalizeTagList = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value ?? "").trim())
      .filter(Boolean),
  ),
];

const CreatableMultiSelect = ({
  label,
  options,
  value,
  inputValue,
  onInputChange,
  onChange,
  onCommitPending,
  loading,
}) => (
  <Autocomplete
    multiple
    freeSolo
    disableCloseOnSelect
    selectOnFocus
    clearOnBlur={false}
    handleHomeEndKeys
    options={options}
    loading={loading}
    value={value || []}
    inputValue={inputValue}
    onInputChange={(_, nextValue) => onInputChange(nextValue)}
    onChange={(_, nextValue) => onChange(normalizeTagList(nextValue))}
    onBlur={onCommitPending}
    renderTags={(selected, getTagProps) =>
      selected.map((option, index) => (
        <Chip
          {...getTagProps({ index })}
          key={`${option}-${index}`}
          label={option}
          size="small"
          sx={chipSx}
        />
      ))
    }
    renderOption={(props, option, { selected }) => {
      const { key, ...optionProps } = props;
      return (
        <li key={key} {...optionProps}>
          <Checkbox
            checked={selected}
            sx={{ color: "rgba(255,255,255,0.5)", p: 0.5, mr: 1 }}
          />
          <ListItemText primary={option} />
        </li>
      );
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={`Select or create ${label.toLowerCase()}`}
        sx={creatableFieldFlex}
      />
    )}
    slotProps={{
      paper: { sx: autocompletePaperSx },
      // popper: {
      //   placement: "bottom-start",
      //   modifiers: [
      //     {
      //       name: "flip",
      //       enabled: false,
      //     },
      //   ],
      // },
    }}
    sx={{ flex: 1, minWidth: 0 }}
  />
);

const SignupForm = ({ formData, handleChange, handleSubmit, loading: submitting }) => {
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [globalRoleOptions, setGlobalRoleOptions] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [expertiseOptions, setExpertiseOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [skillsInput, setSkillsInput] = useState("");
  const [expertiseInput, setExpertiseInput] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [jobTitlesList, globalRolesList, skillsList, expertiseList, rolesList] =
          await Promise.all([
            getAllJobTitles(),
            getAllGlobalRoles(),
            getAllSkills(),
            getAllExpertise(),
            getRoles(),
          ]);
        setJobTitleOptions(jobTitlesList);
        setGlobalRoleOptions(globalRolesList);
        setSkillOptions(skillsList);
        setExpertiseOptions(expertiseList);
        const roles = rolesList?.data?.data || [];
        setRoleOptions(roles);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const skillNames = skillOptions.map((s) => s.skillName).filter(Boolean);
  const expertiseNames = expertiseOptions
    .map((e) => e.expertiseName)
    .filter(Boolean);

  const commitPendingTags = (fieldName, currentValues, pendingInput, clearInput) => {
    const pending = String(pendingInput ?? "").trim();
    if (!pending) return currentValues || [];

    const next = normalizeTagList([...(currentValues || []), pending]);
    handleChange({ target: { name: fieldName, value: next } });
    clearInput("");
    return next;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const skills = commitPendingTags(
      "skills",
      formData.skills,
      skillsInput,
      setSkillsInput,
    );
    const expertise = commitPendingTags(
      "expertise",
      formData.expertise,
      expertiseInput,
      setExpertiseInput,
    );

    handleSubmit(e, { skills, expertise });
  };

  return (
    <Box
      component="form"
      onSubmit={handleFormSubmit}
      noValidate
      sx={{ width: "100%", minWidth: 0 }}
    >
      <Stack spacing={1.75} sx={{ minWidth: 0 }}>
        <FieldRow>
          <TextField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            fullWidth
            sx={fieldFlex}
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            fullWidth
            sx={fieldFlex}
            required
          />
        </FieldRow>

        <FieldRow>
          <TextField
            label="Email Address"
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            fullWidth
            sx={fieldFlex}
            required
          />
          <TextField
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            fullWidth
            sx={fieldFlex}
            required
          />
        </FieldRow>

        <FieldRow>
          <FormControl fullWidth sx={fieldFlex} required>
            <InputLabel>Job Title</InputLabel>
            <Select
              multiple
              value={formData.jobTitles || []}
              label="Job Title"
              onChange={(e) =>
                handleChange({
                  target: { name: "jobTitles", value: e.target.value },
                })
              }
              renderValue={(selected) =>
                renderMultiSelectValue(
                  selected,
                  jobTitleOptions,
                  (jobTitle) => jobTitle.jobTitleId,
                  (jobTitle) => jobTitle.jobTitleName,
                )
              }
              MenuProps={selectMenuProps}
            >
              {loading ? (
                <MenuItem disabled sx={{ justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </MenuItem>
              ) : (
                jobTitleOptions.map((jobTitle) => (
                  <MenuItem key={jobTitle.jobTitleId} value={jobTitle.jobTitleId}>
                    <Checkbox
                      checked={(formData.jobTitles || []).includes(jobTitle.jobTitleId)}
                      sx={{ color: "rgba(255,255,255,0.5)", p: 0.5, mr: 1 }}
                    />
                    <ListItemText primary={jobTitle.jobTitleName} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={fieldFlex} required>
            <InputLabel>Role</InputLabel>
            <Select
              multiple
              value={formData.globalRoles || []}
              label="Role"
              onChange={(e) =>
                handleChange({
                  target: { name: "globalRoles", value: e.target.value },
                })
              }
              renderValue={(selected) =>
                renderMultiSelectValue(
                  selected,
                  globalRoleOptions,
                  (globalRole) => globalRole.roleId,
                  (globalRole) => globalRole.roleName,
                )
              }
              MenuProps={selectMenuProps}
            >
              {loading ? (
                <MenuItem disabled sx={{ justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </MenuItem>
              ) : (
                globalRoleOptions.map((globalRole) => (
                  <MenuItem key={globalRole.roleId} value={globalRole.roleId}>
                    <Checkbox
                      checked={(formData.globalRoles || []).includes(globalRole.roleId)}
                      sx={{ color: "rgba(255,255,255,0.5)", p: 0.5, mr: 1 }}
                    />
                    <ListItemText primary={globalRole.roleName} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </FieldRow>

        <FieldRow>
          <CreatableMultiSelect
            label="Skills"
            options={skillNames}
            value={formData.skills}
            inputValue={skillsInput}
            onInputChange={setSkillsInput}
            loading={loading}
            onChange={(next) => {
              handleChange({ target: { name: "skills", value: next } });
              setSkillsInput("");
            }}
            onCommitPending={() =>
              commitPendingTags(
                "skills",
                formData.skills,
                skillsInput,
                setSkillsInput,
              )
            }
          />

          <CreatableMultiSelect
            label="Expertise"
            options={expertiseNames}
            value={formData.expertise}
            inputValue={expertiseInput}
            onInputChange={setExpertiseInput}
            loading={loading}
            onChange={(next) => {
              handleChange({ target: { name: "expertise", value: next } });
              setExpertiseInput("");
            }}
            onCommitPending={() =>
              commitPendingTags(
                "expertise",
                formData.expertise,
                expertiseInput,
                setExpertiseInput,
              )
            }
          />
        </FieldRow>

        <FieldRow>
          <FormControl fullWidth sx={fieldFlex} required>
            <InputLabel>User Roles</InputLabel>
            <Select
              multiple={false}
              value={formData.userRoles || {}}
              label="User Roles"
              onChange={(e) => {
                handleChange({
                  target: { name: "userRoles", value: e.target.value },
                });
              }}
              renderValue={(value) => value.name}
              MenuProps={selectMenuProps}
            >
              {loading ? (
                <MenuItem disabled sx={{ justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: "white" }} />
                </MenuItem>
              ) : (
                roleOptions.map((role) => (
                  <MenuItem key={role.id} value={role}>
                    <Checkbox
                      checked={formData.userRoles?.id === role.id}
                      sx={{ color: "rgba(255,255,255,0.5)", p: 0.5, mr: 1 }}
                    />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </FieldRow>

        <Button
          type="submit"
          variant="contained"
          disabled={submitting}
          sx={{
            width: { xs: "100%", sm: "170px" },
            maxWidth: "100%",
            mt: 0.5,
            alignSelf: "center",
            py: 1,
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            borderRadius: 2,
            background: "#9c27b0",
            boxShadow: "0 4px 14px 0 rgba(156, 39, 176, 0.4)",
            "&:hover": {
              background: "#7b1fa2",
              boxShadow: "0 6px 20px 0 rgba(156, 39, 176, 0.5)",
            },
          }}
        >
          {submitting ? "Creating..." : "Create User"}
        </Button>
      </Stack>
    </Box>
  );
};

export default SignupForm;
